import { LAYOUT_SIDEBAR_TYPES } from "../Components/constants/layout";

export const SHELL_VISUAL_PREVIEW_STORAGE_KEY = "adatis.shellVisualPreview";
export const SHELL_VISUAL_PREVIEW_EVENT = "adatis:shell-visual-preview";
export const SHELL_VISUAL_PREVIEW_ATTR = "data-adatis-shell-preview";

/** Product default = light contrast (Visual Lab B, approved 01/11/2026). */
export const SHELL_VISUAL_PREVIEW_VARIANTS = [
  "current",
  "sidebar-dark",
] as const;

export type ShellVisualPreviewVariant =
  (typeof SHELL_VISUAL_PREVIEW_VARIANTS)[number];

/** Legacy lab keys that now map to the promoted default. */
const LEGACY_DEFAULT_ALIASES = new Set(["light-contrast"]);

export const isShellVisualPreviewVariant = (
  value: unknown
): value is ShellVisualPreviewVariant =>
  typeof value === "string" &&
  (SHELL_VISUAL_PREVIEW_VARIANTS as readonly string[]).includes(value);

const normalizeStoredVariant = (
  raw: string | null
): ShellVisualPreviewVariant => {
  if (raw === null || LEGACY_DEFAULT_ALIASES.has(raw)) {
    return "current";
  }

  return isShellVisualPreviewVariant(raw) ? raw : "current";
};

export const readShellVisualPreview = (): ShellVisualPreviewVariant => {
  if (typeof window === "undefined") {
    return "current";
  }

  try {
    const raw = window.localStorage.getItem(SHELL_VISUAL_PREVIEW_STORAGE_KEY);
    const normalized = normalizeStoredVariant(raw);

    // Drop legacy "light-contrast" now that B is the product default.
    if (raw !== null && normalized === "current" && raw !== "current") {
      window.localStorage.removeItem(SHELL_VISUAL_PREVIEW_STORAGE_KEY);
    }

    return normalized;
  } catch {
    return "current";
  }
};

export const sidebarThemeForPreview = (
  variant: ShellVisualPreviewVariant
): (typeof LAYOUT_SIDEBAR_TYPES)[keyof typeof LAYOUT_SIDEBAR_TYPES] =>
  variant === "sidebar-dark"
    ? LAYOUT_SIDEBAR_TYPES.DARK
    : LAYOUT_SIDEBAR_TYPES.LIGHT;

export const applyShellVisualPreviewDom = (
  variant: ShellVisualPreviewVariant
): void => {
  if (typeof document === "undefined") {
    return;
  }

  if (variant === "current") {
    document.documentElement.removeAttribute(SHELL_VISUAL_PREVIEW_ATTR);
  } else {
    document.documentElement.setAttribute(SHELL_VISUAL_PREVIEW_ATTR, variant);
  }

  document.documentElement.setAttribute(
    "data-sidebar",
    sidebarThemeForPreview(variant)
  );
};

export const persistShellVisualPreview = (
  variant: ShellVisualPreviewVariant
): void => {
  if (typeof window === "undefined") {
    return;
  }

  try {
    if (variant === "current") {
      window.localStorage.removeItem(SHELL_VISUAL_PREVIEW_STORAGE_KEY);
    } else {
      window.localStorage.setItem(SHELL_VISUAL_PREVIEW_STORAGE_KEY, variant);
    }
  } catch {
    // ignore quota / private mode
  }

  applyShellVisualPreviewDom(variant);
  window.dispatchEvent(
    new CustomEvent(SHELL_VISUAL_PREVIEW_EVENT, { detail: { variant } })
  );
};

export const isShellVisualPreviewActive = (
  variant: ShellVisualPreviewVariant = readShellVisualPreview()
): boolean => variant !== "current";
